import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { fills, type FillsQuery } from "@/api/fills";
import { useActiveAccount } from "@/components/ActiveAccount";
import { downloadCsv } from "@/lib/csv";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  Label,
  Select,
} from "@/components/ui";

/**
 * Per-trading-account fills history. Defaults to the active account;
 * admins can clear the filter to see every fill in the org.
 */
export function FillsPage() {
  const { active } = useActiveAccount();
  const [filters, setFilters] = useState<FillsQuery>({
    trading_account_id: active?.id,
    limit: 200,
  });
  const list = useQuery({
    queryKey: ["fills", filters],
    queryFn: () => fills.list(filters),
    refetchInterval: 30_000,
  });

  function update<K extends keyof FillsQuery>(k: K, v: FillsQuery[K]) {
    setFilters((cur) => ({ ...cur, [k]: v || undefined }));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Fills</h1>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            disabled={!list.data?.length}
            onClick={() => {
              if (!list.data?.length) return;
              downloadCsv(
                list.data as unknown as Record<string, unknown>[],
                `fills-${new Date().toISOString().slice(0, 10)}`,
              );
            }}
          >
            Export CSV
          </Button>
          <Button onClick={() => list.refetch()}>Refresh</Button>
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
            <div>
              <Label>Symbol</Label>
              <Input
                value={filters.symbol ?? ""}
                onChange={(e) => update("symbol", e.target.value.toUpperCase())}
                placeholder="EUR/USD"
              />
            </div>
            <div>
              <Label>Venue</Label>
              <Select
                value={filters.venue ?? ""}
                onChange={(e) => update("venue", e.target.value)}
              >
                <option value="">any</option>
                <option value="INTERNAL">INTERNAL (L1)</option>
                <option value="INTERNAL_L2">INTERNAL_L2</option>
                <option value="INTERNAL_L2_MAKER">INTERNAL_L2_MAKER</option>
                <option value="STOP_OUT">STOP_OUT</option>
                <option value="STOP_TRIGGER">STOP_TRIGGER</option>
              </Select>
            </div>
            <div>
              <Label>From</Label>
              <Input
                type="datetime-local"
                value={filters.from_ts ?? ""}
                onChange={(e) => update("from_ts", e.target.value)}
              />
            </div>
            <div>
              <Label>To</Label>
              <Input
                type="datetime-local"
                value={filters.to_ts ?? ""}
                onChange={(e) => update("to_ts", e.target.value)}
              />
            </div>
            <div>
              <Label>Account</Label>
              <Input
                value={filters.trading_account_id ?? ""}
                onChange={(e) => update("trading_account_id", e.target.value)}
                placeholder={active?.id ?? "uuid"}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !list.data || list.data.length === 0 ? (
            <EmptyState
              title="No fills"
              description="No fills match these filters. Trade through the terminal to populate."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Time</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Symbol</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Side</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Qty</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Price</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Commission</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Venue</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((f) => (
                  <tr key={f.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                      {new Date(f.timestamp).toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-term-fg">{f.symbol}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={f.side === "BUY" ? "good" : "warn"}>{f.side}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{f.quantity}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{f.price}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{f.commission}</td>
                    <td className="px-3 py-1.5 text-2xs text-term-dim">{f.venue ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
